import type { HumanVerification, RenderOptions, VerificationHandle } from "./types";

/**
 * The REAL [HumanVerification] — Cloudflare Turnstile. The ONLY module in the whole frontend that
 * references the Turnstile script/API (the CSP delta on `/v/*` allows `challenges.cloudflare.com`); apps
 * import the [HumanVerification] port, never this. Cookieless + non-interactive (consent-free — no CNIL
 * banner). The script is loaded lazily ON FIRST render (the happy path never fetches it — R1/R5).
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileApi {
  render(container: HTMLElement, params: Record<string, unknown>): string;
  remove(widgetId: string): void;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => reject(new Error("turnstile-script-failed")));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export class TurnstileVerification implements HumanVerification {
  render(container: HTMLElement, opts: RenderOptions): VerificationHandle {
    let widgetId: string | null = null;
    let removed = false;

    void loadScript()
      .then(() => {
        if (removed || !window.turnstile) return;
        widgetId = window.turnstile.render(container, {
          sitekey: opts.sitekey,
          callback: (token: string) => opts.onToken(token),
          "error-callback": () => opts.onError?.("turnstile-error"),
          "expired-callback": () => opts.onError?.("turnstile-expired"),
          language: opts.language ?? "auto",
          appearance: "interaction-only",
        });
      })
      .catch(() => opts.onError?.("turnstile-load-failed"));

    return {
      remove() {
        removed = true;
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      },
      reset() {
        if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
      },
    };
  }
}

/** The default shared instance apps consume through the port. */
export const turnstileVerification: HumanVerification = new TurnstileVerification();
